import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiCreateWorkspace, apiFetchWorkspaces, type WorkspaceListItem } from "../lib/authApi";
import {
  clearWorkspaceNavSessionCache,
  commitWorkspaceNavSessionCache,
  getWorkspaceNavSessionSnapshot,
} from "../lib/workspaceNavSessionCache";
import { usePins } from "../app/context/PinsContext";
import { useLongPress, type LongPressCoords } from "../hooks/useLongPress";
import { AppIcon } from "./AppIcon";
import { SidebarContextMenu } from "./SidebarContextMenu";

type WorkspaceNavSectionProps = {
  enabled: boolean;
  selectedWorkspaceId: string | null;
  onSelectWorkspaceRow: (workspace: WorkspaceListItem) => void;
  /** Mobile: section collapse + chevron in header */
  collapsible?: boolean;
  /** When collapsible: controlled expanded state */
  expanded?: boolean;
  onToggleExpanded?: () => void;
  /** Bumped when workspace topic activity should refresh unread totals in this list. */
  listRefreshEpoch?: number;
};

export function WorkspaceNavSection({
  enabled,
  selectedWorkspaceId,
  onSelectWorkspaceRow,
  collapsible = false,
  expanded = true,
  onToggleExpanded,
  listRefreshEpoch = 0,
}: WorkspaceNavSectionProps) {
  const [navInit] = useState(() => getWorkspaceNavSessionSnapshot());
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>(navInit.list);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const createInputRef = useRef<HTMLInputElement>(null);
  // Per-instance: restored from session module so remount after sidebar hide does not flash "загрузка…".
  const hasLoadedRef = useRef(navInit.hasLoaded);

  useEffect(() => {
    if (!enabled) {
      clearWorkspaceNavSessionCache();
      hasLoadedRef.current = false;
      setWorkspaces([]);
      setError(null);
      return;
    }
    let cancelled = false;
    if (!hasLoadedRef.current) {
      setListLoading(true);
    }
    setError(null);
    void apiFetchWorkspaces()
      .then(({ workspaces: list }) => {
        if (cancelled) return;
        commitWorkspaceNavSessionCache(list);
        setWorkspaces(list);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        clearWorkspaceNavSessionCache();
        hasLoadedRef.current = false;
        setError(e instanceof Error ? e.message : String(e));
        setWorkspaces([]);
      })
      .finally(() => {
        if (cancelled) return;
        hasLoadedRef.current = true;
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, listRefreshEpoch]);

  useEffect(() => {
    if (creating) createInputRef.current?.focus();
  }, [creating]);

  const onPickWorkspace = useCallback(
    (w: WorkspaceListItem) => {
      onSelectWorkspaceRow(w);
    },
    [onSelectWorkspaceRow],
  );

  const submitCreate = useCallback(async () => {
    const name = createName.trim();
    if (!name || createBusy) return;
    setCreateBusy(true);
    setError(null);
    try {
      const { workspace } = await apiCreateWorkspace(name);
      const item: WorkspaceListItem = {
        ...workspace,
        joined_at: workspace.created_at,
        unread_count: workspace.unread_count ?? 0,
      };
      setWorkspaces((prev) => {
        const next = [...prev, item];
        commitWorkspaceNavSessionCache(next);
        return next;
      });
      setCreating(false);
      setCreateName("");
      onSelectWorkspaceRow(item);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreateBusy(false);
    }
  }, [createName, createBusy, onSelectWorkspaceRow]);

  const { isPinned, getPinnedAt } = usePins();

  const sortedWorkspaces = useMemo(() => {
    const list = [...workspaces];
    list.sort((a, b) => {
      const ap = isPinned("workspace", a.id) ? 1 : 0;
      const bp = isPinned("workspace", b.id) ? 1 : 0;
      if (ap !== bp) return bp - ap;
      if (ap && bp) return (getPinnedAt("workspace", a.id) ?? 0) - (getPinnedAt("workspace", b.id) ?? 0);
      return 0;
    });
    return list;
  }, [workspaces, isPinned, getPinnedAt]);

  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; workspaceId: string } | null>(null);
  const onLongPressRow = useCallback(
    (workspaceId: string) => (coords: LongPressCoords) => {
      setCtxMenu({ ...coords, workspaceId });
    },
    [],
  );

  const workspaceUnreadTotal = useMemo(
    () => workspaces.reduce((s, w) => s + (w.unread_count ?? 0), 0),
    [workspaces],
  );

  if (!enabled) return null;

  const headContent = (
    <>
      {collapsible ? (
        <button
          type="button"
          className="workspace-nav__head-toggle"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
        >
          <span className="workspace-nav__title" title="// Workspaces">
            // Workspaces
          </span>
          <AppIcon
            name={expanded ? "chevron-down" : "chevron-up"}
            size={19}
            className="sidebar-section__chevron"
          />
        </button>
      ) : (
        <span className="workspace-nav__title" title="// Workspaces">
          // Workspaces
        </span>
      )}
      <div className="workspace-nav__head-right sidebar-section__head-actions">
        {collapsible && !expanded && workspaceUnreadTotal > 0 ? (
          <span className="contact-row__badge workspace-nav__unread-badge" aria-label="Непрочитано">
            {workspaceUnreadTotal}
          </span>
        ) : null}
        {workspaces.length > 0 ? (
          <span className="workspace-nav__meta">{workspaces.length}</span>
        ) : null}
        <button
          type="button"
          className="workspace-nav__add-btn sidebar-section__action-btn"
          title="Создать workspace"
          onClick={(e) => {
            e.stopPropagation();
            setCreating((v) => !v);
          }}
        >
          <AppIcon name="plus" className="workspace-nav__add-icon sidebar-section__action-icon" size={16} />
        </button>
      </div>
    </>
  );

  return (
    <div className="sidebar-workspace-nav">
      <div className="sidebar-section">
        <div className={`workspace-nav__head sidebar-section__toolbar ${collapsible ? "workspace-nav__head--collapsible" : ""}`}>
          {headContent}
        </div>
        <div
          className={`sidebar-section__body-wrap ${collapsible ? (expanded ? "is-expanded" : "") : "is-expanded"}`}
        >
          <div className="sidebar-section__body-inner">
            {error ? <p className="workspace-nav__err">{error}</p> : null}
            {creating && expanded ? (
              <form
                className="workspace-nav__create-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitCreate();
                }}
              >
                <input
                  ref={createInputRef}
                  className="workspace-nav__create-input"
                  type="text"
                  placeholder="имя workspace…"
                  autoComplete="off"
                  maxLength={128}
                  value={createName}
                  disabled={createBusy}
                  onChange={(e) => setCreateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setCreating(false);
                      setCreateName("");
                    }
                  }}
                />
                <button type="submit" className="workspace-nav__create-ok" disabled={!createName.trim() || createBusy}>
                  OK
                </button>
              </form>
            ) : null}
            {listLoading ? (
              <p className="workspace-nav__hint">загрузка…</p>
            ) : workspaces.length === 0 && !creating ? (
              <p className="workspace-nav__hint">нет доступных workspaces</p>
            ) : (
              <>
                <ul className="contact-list workspace-nav__list">
                  {sortedWorkspaces.map((w) => (
                    <WorkspaceRow
                      key={w.id}
                      workspace={w}
                      isSelected={w.id === selectedWorkspaceId}
                      pinned={isPinned("workspace", w.id)}
                      onPick={() => onPickWorkspace(w)}
                      onLongPress={onLongPressRow(w.id)}
                    />
                  ))}
                </ul>
                {ctxMenu ? (
                  <SidebarContextMenu
                    x={ctxMenu.x}
                    y={ctxMenu.y}
                    mode="personal"
                    entityType="workspace"
                    entityId={ctxMenu.workspaceId}
                    onClose={() => setCtxMenu(null)}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkspaceRow({
  workspace: w,
  isSelected,
  pinned,
  onPick,
  onLongPress,
}: {
  workspace: WorkspaceListItem;
  isSelected: boolean;
  pinned: boolean;
  onPick: () => void;
  onLongPress: (coords: LongPressCoords) => void;
}) {
  const lp = useLongPress(onLongPress);
  const initial = w.name.trim().charAt(0).toUpperCase() || "#";
  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        className={`contact-row workspace-nav__row ${isSelected ? "is-selected" : ""}`}
        onClick={onPick}
        onKeyDown={(e) => { if (e.key === "Enter") onPick(); }}
        {...lp}
      >
        <div className="contact-avatar">
          <span className="contact-avatar__letter">{initial}</span>
        </div>
        <div className="contact-row__info workspace-nav__row-info">
          <span className="contact-row__name workspace-nav__row-name" title={w.name}>
            {pinned ? <AppIcon name="pin" size={12} className="contact-row__pin-icon" /> : null}
            {w.name}
          </span>
          <span className="workspace-nav__role">{w.role}</span>
        </div>
        {w.unread_count > 0 ? (
          <div className="contact-row__tail workspace-nav__row-tail">
            <span className="contact-row__badge workspace-nav__ws-badge" aria-label="Непрочитано">
              {w.unread_count}
            </span>
          </div>
        ) : null}
      </div>
    </li>
  );
}
